import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert } from "@/components/ui/Alert";
import { Dialog } from "@/components/ui/Dialog";
import { ApiFieldErrors } from "@/components/ui/ApiFieldErrors";
import { apiFieldDescribedBy, apiFieldHasError } from "@/components/ui/api-field-errors";
import { usePromoteRelease } from "../hooks/useDeployments";
import { isReleaseHalted } from "../lib/halt-history";
import type { PackageInfo } from "../api/deployments.api";

interface PromoteDialogProps {
  appName: string;
  sourceDeploymentName: string;
  sourceDisabled: boolean;
  destinations: string[];
  open: boolean;
  onClose: () => void;
}

export function PromoteDialog({
  appName,
  sourceDeploymentName,
  sourceDisabled,
  destinations,
  open,
  onClose,
}: PromoteDialogProps) {
  const { t } = useTranslation();
  const promote = usePromoteRelease(appName, sourceDeploymentName);
  const [chosenDestination, setChosenDestination] = useState<string | null>(null);
  const [rollout, setRollout] = useState("");
  const fieldErrorPrefix = useId();
  const sourceDisabledId = useId();
  const destination =
    chosenDestination !== null && destinations.includes(chosenDestination)
      ? chosenDestination
      : (destinations[0] ?? "");
  const haltRefused = isReleaseHalted(promote.error);
  const fieldError = haltRefused ? null : promote.error;
  const rolloutInvalid = apiFieldHasError(fieldError, "rollout");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (promote.isPending) return;
    if (!destination) return;

    const packageInfo: PackageInfo = {};
    const rolloutValue = Number(rollout);
    if (rollout.trim() !== "" && rolloutValue >= 1 && rolloutValue <= 100) {
      packageInfo.rollout = rolloutValue;
    }

    promote.mutate(
      { destination, packageInfo },
      {
        onSuccess: () => {
          setRollout("");
          onClose();
        },
      },
    );
  };

  const clearFieldError = () => {
    if (fieldError) promote.reset();
  };

  const handleRolloutChange = (value: string) => {
    setRollout(value);
    clearFieldError();
  };

  const handleClose = () => {
    if (!promote.isPending) {
      promote.reset();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t("deployments.promote")}
      description={t("deployments.promoteDescription", { source: sourceDeploymentName })}
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="promote-destination" className="text-sm font-medium text-subtle">
            {t("deployments.destinationLabel")}
          </label>
          <select
            id="promote-destination"
            value={destination}
            onChange={(e) => {
              if (promote.isPending) return;
              setChosenDestination(e.target.value);
              clearFieldError();
            }}
            aria-disabled={promote.isPending || undefined}
            className="h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading transition-colors focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo disabled:opacity-50 aria-disabled:cursor-not-allowed aria-disabled:opacity-50 hover:border-border-light"
          >
            {destinations.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="promote-rollout" className="text-sm font-medium text-subtle">
            {t("deployments.rolloutOptionalLabel")}
          </label>
          <input
            id="promote-rollout"
            type="number"
            min={1}
            max={100}
            value={rollout}
            onChange={(e) => handleRolloutChange(e.target.value)}
            placeholder="100"
            readOnly={promote.isPending}
            aria-disabled={promote.isPending || undefined}
            aria-invalid={rolloutInvalid ? true : undefined}
            aria-describedby={apiFieldDescribedBy(fieldError, "rollout", fieldErrorPrefix)}
            className="h-11 w-full rounded-lg border border-border bg-surface px-3.5 text-sm text-heading transition-colors placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-indigo/40 focus:border-indigo aria-disabled:cursor-not-allowed aria-disabled:opacity-50 hover:border-border-light"
          />
        </div>

        {haltRefused && (
          <Alert variant="warning">
            <p>{t("deployments.promoteHalted", { source: sourceDeploymentName })}</p>
          </Alert>
        )}

        {sourceDisabled && destination && (
          <p id={sourceDisabledId} className="text-xs text-muted">
            {t("deployments.promoteSourceDisabled", {
              source: sourceDeploymentName,
              destination,
            })}
          </p>
        )}

        <ApiFieldErrors
          error={fieldError}
          fieldLabels={{ rollout: t("deployments.rolloutOptionalLabel") }}
          idPrefix={fieldErrorPrefix}
        />

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={handleClose}
            aria-disabled={promote.isPending || undefined}
            className="rounded-lg border border-border bg-surface px-4 py-2.5 text-sm font-medium text-muted transition-colors hover:border-border-light hover:text-heading aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={!promote.isPending && !destination}
            aria-disabled={promote.isPending || undefined}
            aria-busy={promote.isPending || undefined}
            data-autofocus={promote.isPending ? true : undefined}
            aria-describedby={sourceDisabled && destination ? sourceDisabledId : undefined}
            className="rounded-lg bg-indigo px-4 py-2.5 text-sm font-semibold text-white transition-all hover:bg-indigo-bright disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:cursor-not-allowed"
          >
            {promote.isPending ? t("deployments.promoting") : t("deployments.promote")}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
